import type { IConversationMessage } from '../hooks/useAssistantWebSocket';
import type { SnapshotMessage } from '@orn/global/assistant/snapshotMessage.js';

export type { SnapshotMessage };

/**
 * Build a fresh `IConversationMessage[]` from a server `transcript_snapshot`.
 *
 * Snapshot-derived messages are never pending (`isPending` omitted) and
 * carry `clientMessageId` directly off the wire row (`undefined` when the
 * server didn't populate one, e.g. eval-path rows).
 *
 * `generateId` is injected so callers can stub it in tests and the hook can
 * pass its own time+random implementation in production.
 */
export function snapshotToConversation(snapshot: SnapshotMessage[], generateId: () => string): IConversationMessage[] {
  const out: IConversationMessage[] = [];
  for (const row of snapshot) {
    switch (row.kind) {
      case 'user':
        out.push({
          id: `msg-${generateId()}`,
          role: 'user',
          content: row.content,
          timestamp: new Date(row.timestamp),
          clientMessageId: row.clientMessageId,
          ...(row.attachments && row.attachments.length > 0 ? { attachments: row.attachments } : {}),
        });
        break;
      case 'assistant':
        out.push({
          id: `msg-${generateId()}`,
          role: 'assistant',
          content: row.content,
          timestamp: new Date(row.timestamp),
        });
        break;
      case 'tool_call': {
        // Collapse consecutive identical goals (parallel calls to the same tool —
        // e.g. one `math_resolve` per category) into a single gray line. Mirrors
        // the live `insertToolCall` dedup so snapshot and live render the same.
        const last = out[out.length - 1];
        if (last?.kind === 'tool-call' && last.content === row.goal) {
          break;
        }
        out.push({
          id: `tool-call-${row.toolId}`,
          role: 'assistant',
          kind: 'tool-call',
          toolId: row.toolId,
          content: row.goal,
          timestamp: new Date(row.timestamp),
        });
        break;
      }
      default: {
        // Compile-time exhaustiveness check: adding a new `SnapshotMessage`
        // variant without a case branch fails here.
        const _exhaustive: never = row;
        void _exhaustive;
      }
    }
  }
  return out;
}

/**
 * Reconcile prior in-memory messages against a server-authoritative
 * `transcript_snapshot`: replace the list with the snapshot, then re-append
 * any pending user bubble (`isPending: true`) whose `clientMessageId` does
 * not appear on a `snapshot.user` row.
 *
 * Identity match by `clientMessageId` is the only confirmation path —
 * production web clients always populate it, and the snapshot reconciler
 * is only ever exercised against real server snapshots.
 *
 * Non-pending bubbles defer entirely to the snapshot — the server is
 * authoritative for already-confirmed history. The caller passes the raw
 * `SnapshotMessage[]` alongside the converted list so this reconciler can
 * read `clientMessageId` directly off the wire row without re-deriving.
 */
export function reconcileSnapshotMessages(
  prev: IConversationMessage[],
  snapshotMessages: IConversationMessage[],
  snapshotRaw: SnapshotMessage[]
): IConversationMessage[] {
  const seenIds = new Set<string>();
  for (const row of snapshotRaw) {
    if (row.kind === 'user' && row.clientMessageId !== undefined) {
      seenIds.add(row.clientMessageId);
    }
  }
  const preserved = prev.filter(
    (m) => m.isPending === true && (m.clientMessageId === undefined || !seenIds.has(m.clientMessageId))
  );
  return [...snapshotMessages, ...preserved];
}

/**
 * Confirm every pending bubble whose `clientMessageId` appears in
 * `clientMessageIds`. Used when the server replies to a turn — the
 * `assistant_message` echoes the full id array (one for single-message
 * turns, N for batched turns) and all matching bubbles flip from pending
 * to confirmed in a single pass.
 *
 * Returns a NEW array. Bubbles whose ids are not in the set, and non-user /
 * already-confirmed bubbles, are left untouched. Empty `clientMessageIds`
 * (eval-path turn without ids) returns the input unchanged.
 */
export function confirmPendingMessages(
  messages: IConversationMessage[],
  clientMessageIds: string[]
): IConversationMessage[] {
  if (clientMessageIds.length === 0) {
    return messages;
  }
  const idSet = new Set(clientMessageIds);
  let changed = false;
  const next = messages.map((m) => {
    if (m.role === 'user' && m.isPending === true && m.clientMessageId !== undefined && idSet.has(m.clientMessageId)) {
      changed = true;
      return { ...m, isPending: false };
    }
    return m;
  });
  return changed ? next : messages;
}

/**
 * Index just past the in-flight turn's items — the position of the first
 * *queued* user bubble (`isPending && !isInFlight`), or `messages.length` when
 * none is queued. Queued bubbles are always the trailing run (you can only
 * queue a message while a turn is already in flight), so this index is the
 * boundary between "items belonging to the current turn" and "what the user
 * typed to send next."
 */
function inFlightSectionEnd(messages: IConversationMessage[]): number {
  const firstQueued = messages.findIndex((m) => m.isPending === true && m.isInFlight !== true);
  return firstQueued === -1 ? messages.length : firstQueued;
}

function insertAtInFlightSectionEnd(
  messages: IConversationMessage[],
  item: IConversationMessage
): IConversationMessage[] {
  const i = inFlightSectionEnd(messages);
  return [...messages.slice(0, i), item, ...messages.slice(i)];
}

/**
 * Insert an assistant reply bubble at the end of the in-flight section (see
 * {@link inFlightSectionEnd}). The stream-from-the-server protocol emits N
 * `assistant_message` frames per turn (one per round with prose); each appends
 * after the prior in-flight items — the in-flight user message(s), earlier
 * rounds' bubbles, and any tool-call lines — and above any bubble the user
 * queued mid-turn. Arrival order in, arrival order out: a turn that runs tools
 * then answers renders `user → goal… → goal… → answer`.
 *
 * Returns a NEW array. Pure function — no React, no refs, fully unit-testable.
 */
export function insertAssistantReply(
  messages: IConversationMessage[],
  reply: IConversationMessage
): IConversationMessage[] {
  return insertAtInFlightSectionEnd(messages, reply);
}

/**
 * Insert a tool-call line at the same in-flight-section slot as a reply, so
 * live tool-call lines and the answer they precede render in arrival order (tool-call lines first).
 *
 * Returns a NEW array. Pure — no React, fully unit-testable.
 */
export function insertToolCall(messages: IConversationMessage[], goal: IConversationMessage): IConversationMessage[] {
  // Collapse parallel calls to the same tool: a batch of identical goals (e.g.
  // one `math_resolve` per category) arrives as consecutive `tool_started`
  // frames — render a single gray line instead of N copies. Only the line
  // immediately preceding the insertion slot is checked, so a genuinely later
  // round that repeats the goal (after a reply bubble) still shows its own line.
  const end = inFlightSectionEnd(messages);
  const preceding = end > 0 ? messages[end - 1] : undefined;
  if (preceding?.kind === 'tool-call' && preceding.content === goal.content) {
    return messages;
  }
  return insertAtInFlightSectionEnd(messages, goal);
}

// ============================================
// Streaming-bubble lifecycle
// ============================================
//
// Token-level streaming grows one assistant bubble in place across a round's
// `assistant_message_delta` frames, then snaps it to the authoritative text on
// the terminal `assistant_message`. The bubble is seeded by `insertAssistantReply`
// (above) carrying `isStreaming: true`, grown by `appendStreamingDelta`, and then
// either `finalizeStreamingBubble` (success) or `dropStreamingBubble` (error /
// abort mid-stream). All three target the bubble by id, return a NEW array, and
// preserve the object identity of every untouched row so a memoized row list
// re-renders only the bubble that changed.

/**
 * Append a token-level delta to the streaming bubble, concatenating onto its
 * running text. A no-op (input contents unchanged) when no row matches —
 * defensive against a stray delta after the bubble was dropped.
 */
export function appendStreamingDelta(
  messages: IConversationMessage[],
  streamingId: string,
  text: string
): IConversationMessage[] {
  return messages.map((m) => (m.id === streamingId ? { ...m, content: m.content + text } : m));
}

/**
 * Snap the streaming bubble to the round's authoritative full text and clear its
 * `isStreaming` flag. Replacing (not appending) the content drops any client-side
 * accumulation drift between the streamed deltas and the server's canonical text.
 */
export function finalizeStreamingBubble(
  messages: IConversationMessage[],
  streamingId: string,
  content: string
): IConversationMessage[] {
  return messages.map((m) => {
    if (m.id !== streamingId) {
      return m;
    }
    const finalized = { ...m, content };
    delete finalized.isStreaming;
    return finalized;
  });
}

/**
 * Remove an unfinalized streaming bubble (error / abort mid-round). Tolerant of a
 * missing id (returns an equivalent list).
 */
export function dropStreamingBubble(messages: IConversationMessage[], streamingId: string): IConversationMessage[] {
  return messages.filter((m) => m.id !== streamingId);
}
